import React, { useState, useEffect } from 'react';
import Modal, { FormField, FormInput, FormButton } from './Modal';
import { authAPI } from '../lib/api';
import { toast } from '../lib/toast';
import { ShieldCheck, ShieldOff } from 'lucide-react';

export default function MfaModal({ isOpen, onClose }) {
  const [enabled, setEnabled] = useState(null); // null = chargement
  const [step, setStep] = useState('status');    // status | setup | disable
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('status'); setCode(''); setError(''); setQr(''); setSecret(''); setEnabled(null);
      authAPI.mfaStatus().then(r => setEnabled(!!r.data?.enabled)).catch(() => setEnabled(false));
    }
  }, [isOpen]);

  const setCodeSafe = (v) => setCode(v.replace(/\D/g, '').slice(0, 6));

  const startSetup = async () => {
    setLoading(true); setError('');
    try {
      const r = await authAPI.mfaSetup();
      setQr(r.data.qr_data_uri); setSecret(r.data.secret); setCode(''); setStep('setup');
    } catch (err) { setError(err.response?.data?.detail || 'Erreur lors de la préparation'); }
    setLoading(false);
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await authAPI.mfaVerify(code);
      toast.success('Authentification à deux facteurs activée');
      onClose();
    } catch (err) { setError(err.response?.data?.detail || 'Code invalide'); }
    setLoading(false);
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await authAPI.mfaDisable(code);
      toast.success('Authentification à deux facteurs désactivée');
      onClose();
    } catch (err) { setError(err.response?.data?.detail || 'Code invalide'); }
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Authentification à deux facteurs" maxWidth="460px">
      {error && <div className="mb-4 px-3 py-2 rounded-[8px] text-[12px] text-[#DC2626]" style={{ background: 'rgba(220,38,38,0.1)' }}>{error}</div>}

      {enabled === null && <div className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>Chargement…</div>}

      {enabled !== null && step === 'status' && (
        <div>
          <div className="flex items-center gap-2 mb-4 text-[13px]">
            {enabled
              ? <span className="flex items-center gap-2" style={{ color: '#22C55E' }}><ShieldCheck className="w-4 h-4" /> Activée sur votre compte</span>
              : <span className="flex items-center gap-2" style={{ color: 'var(--text-tertiary)' }}><ShieldOff className="w-4 h-4" /> Désactivée</span>}
          </div>
          <p className="text-[12.5px] mb-5" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            Protégez votre compte avec un code à 6 chiffres généré par une application
            (Microsoft Authenticator, Google Authenticator, Authy…), exigé à chaque connexion.
          </p>
          <div className="flex justify-end gap-3">
            <FormButton variant="secondary" onClick={onClose}>Fermer</FormButton>
            {enabled
              ? <FormButton variant="secondary" onClick={() => { setStep('disable'); setCode(''); setError(''); }}>Désactiver</FormButton>
              : <FormButton onClick={startSetup} disabled={loading}>{loading ? '…' : 'Activer'}</FormButton>}
          </div>
        </div>
      )}

      {step === 'setup' && (
        <form onSubmit={confirmSetup}>
          <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-secondary)' }}>
            1. Scannez ce QR code avec votre application d'authentification, puis 2. saisissez le code affiché.
          </p>
          {qr && <div className="flex justify-center mb-3"><img src={qr} alt="QR code MFA" className="w-44 h-44 rounded-[8px] bg-white p-2" /></div>}
          {secret && <div className="text-[11px] text-center mb-4" style={{ color: 'var(--text-tertiary)' }}>Clé manuelle : <code style={{ fontFamily: 'monospace' }}>{secret}</code></div>}
          <FormField label="Code à 6 chiffres" required>
            <FormInput value={code} onChange={(e) => setCodeSafe(e.target.value)} placeholder="000000" required />
          </FormField>
          <div className="flex justify-end gap-3 mt-5">
            <FormButton variant="secondary" onClick={() => setStep('status')}>Retour</FormButton>
            <FormButton type="submit" disabled={loading}>{loading ? 'Vérification…' : 'Activer'}</FormButton>
          </div>
        </form>
      )}

      {step === 'disable' && (
        <form onSubmit={confirmDisable}>
          <p className="text-[12.5px] mb-4" style={{ color: 'var(--text-secondary)' }}>
            Saisissez un code de votre application pour confirmer la désactivation.
          </p>
          <FormField label="Code à 6 chiffres" required>
            <FormInput value={code} onChange={(e) => setCodeSafe(e.target.value)} placeholder="000000" required />
          </FormField>
          <div className="flex justify-end gap-3 mt-5">
            <FormButton variant="secondary" onClick={() => setStep('status')}>Retour</FormButton>
            <FormButton type="submit" disabled={loading}>{loading ? '…' : 'Désactiver'}</FormButton>
          </div>
        </form>
      )}
    </Modal>
  );
}
